// ABOUTME: Shows WhatsApp application flow settings as read-only job-view content.
// ABOUTME: Keeps the job view tab aligned with the card-based vacancy summary.
import React from "react";
import {
    Box,
    Chip,
    Divider,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Typography
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import { WhatsappApplicationFlow } from "./helpers";

interface WhatsappApplicationOption {
    value: string;
    label: string;
}

interface IWhatsappApplicationSummary {
    t: any;
    flow: WhatsappApplicationFlow;
    link: Compleo.IObject | null;
    qualifiedStageOptions: WhatsappApplicationOption[];
    notQualifiedStageOptions: WhatsappApplicationOption[];
    onEdit?: () => void;
    onCopyLink?: () => void;
}

const getOptionLabel = (
    options: WhatsappApplicationOption[],
    value: string,
    fallback: string
) => options.find((option) => option.value === value)?.label || fallback;

const getConditionValue = (value: unknown) =>
    value === undefined || value === null || value === "" ? "-" : String(value);

const SummaryItem = (props: {
    label: string;
    value: React.ReactNode;
    xs?: 12 | 6 | 4 | 3;
}) => (
    <Grid item xs={12} md={props.xs || 4}>
        <Typography variant="body2" color="textSecondary">
            <strong>{props.label}:</strong> {props.value || "-"}
        </Typography>
    </Grid>
);

const WhatsappApplicationSummary = (props: IWhatsappApplicationSummary) => {
    const {
        t,
        flow,
        link,
        qualifiedStageOptions,
        notQualifiedStageOptions,
        onEdit,
        onCopyLink
    } = props;
    const hasActiveFlow = link?.active === true && flow.nodes.length > 0;
    const qualifiedStageLabel = flow.qualifiedStageId
        ? getOptionLabel(
              qualifiedStageOptions,
              flow.qualifiedStageId,
              flow.qualifiedStageId
          )
        : t("whatsappApplicationNoStage");
    const notQualifiedStageLabel = flow.notQualifiedStageId
        ? getOptionLabel(
              notQualifiedStageOptions,
              flow.notQualifiedStageId,
              flow.notQualifiedStageId
          )
        : t("whatsappApplicationNoStage");

    return (
        <Paper elevation={1}>
            <Box padding={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center">
                            <WhatsAppIcon style={{ marginRight: 8 }} />
                            <Typography variant="subtitle1">
                                {t("whatsappApplicationTitle")}
                            </Typography>
                            <Box marginLeft={1}>
                                <Chip
                                    size="small"
                                    color={
                                        link?.active === true
                                            ? "primary"
                                            : "default"
                                    }
                                    label={
                                        link?.active === true
                                            ? t("whatsappApplicationLinkActive")
                                            : t(
                                                  "whatsappApplicationLinkInactive"
                                              )
                                    }
                                />
                            </Box>
                            <Box flexGrow={1} />
                            {onEdit && (
                                <Tooltip title={t("whatsappApplicationEdit")}>
                                    <IconButton onClick={onEdit} size="small">
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Grid>
                    {hasActiveFlow && (
                        <React.Fragment>
                            <SummaryItem
                                label={t("whatsappApplicationFlowName")}
                                value={flow.name}
                            />
                            <SummaryItem
                                label={t("whatsappApplicationQualifiedStageId")}
                                value={qualifiedStageLabel}
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationNotQualifiedStageId"
                                )}
                                value={notQualifiedStageLabel}
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationNotQualifiedReason"
                                )}
                                value={
                                    flow.notQualifiedDisqualifyReason?.label ||
                                    flow.notQualifiedDisqualifyReason?.value
                                }
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationRequiredFieldsSummary"
                                )}
                                value={
                                    flow.requireApplicationFormRequiredFields ===
                                    true
                                        ? t("whatsappApplicationYes")
                                        : t("whatsappApplicationNo")
                                }
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationScreeningQuestionnaireSummary"
                                )}
                                value={
                                    flow.collectScreeningQuestionnaire === true
                                        ? t("whatsappApplicationYes")
                                        : t("whatsappApplicationNo")
                                }
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationApplicationStartText"
                                )}
                                value={flow.applicationStartText}
                                xs={6}
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationExistingCandidateFoundText"
                                )}
                                value={flow.existingCandidateFoundText}
                                xs={6}
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationExistingCandidateInvalidChoiceText"
                                )}
                                value={flow.existingCandidateInvalidChoiceText}
                                xs={4}
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationConsentInvalidAnswerText"
                                )}
                                value={flow.consentInvalidAnswerText}
                                xs={4}
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationResumeInvalidAnswerText"
                                )}
                                value={flow.resumeInvalidAnswerText}
                                xs={4}
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationCvParsingStartedText"
                                )}
                                value={flow.cvParsingStartedText}
                                xs={6}
                            />
                            <SummaryItem
                                label={t(
                                    "whatsappApplicationCvParsingCompletedText"
                                )}
                                value={flow.cvParsingCompletedText}
                                xs={6}
                            />
                            {link?.waMeUrl && (
                                <Grid item xs={12}>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        <strong>
                                            {t(
                                                "whatsappApplicationLinkEnabled"
                                            )}
                                            :
                                        </strong>{" "}
                                        <a
                                            href={String(link.waMeUrl)}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {link.waMeUrl}
                                        </a>
                                        {onCopyLink && (
                                            <Tooltip title={t("copyJobLink")}>
                                                <IconButton
                                                    onClick={onCopyLink}
                                                    size="small"
                                                >
                                                    <FileCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </Typography>
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            {flow.nodes.map((node) => (
                                <Grid item xs={12} key={node.editorKey}>
                                    <Paper variant="outlined">
                                        <Box padding={2}>
                                            <Grid container spacing={1}>
                                                <SummaryItem
                                                    label={t(
                                                        "whatsappApplicationNodeId"
                                                    )}
                                                    value={node.id}
                                                    xs={3}
                                                />
                                                <SummaryItem
                                                    label={t(
                                                        "whatsappApplicationNodeType"
                                                    )}
                                                    value={t(
                                                        `whatsappApplicationNodeType_${node.type}`
                                                    )}
                                                    xs={3}
                                                />
                                                <SummaryItem
                                                    label={t(
                                                        "whatsappApplicationNextNode"
                                                    )}
                                                    value={node.nextNodeId}
                                                    xs={3}
                                                />
                                                <SummaryItem
                                                    label={t(
                                                        "whatsappApplicationNodeText"
                                                    )}
                                                    value={node.text}
                                                    xs={12}
                                                />
                                                {node.answerKey && (
                                                    <SummaryItem
                                                        label={t(
                                                            "whatsappApplicationAnswerKey"
                                                        )}
                                                        value={node.answerKey}
                                                        xs={6}
                                                    />
                                                )}
                                                {node.options.length > 0 && (
                                                    <SummaryItem
                                                        label={t(
                                                            "whatsappApplicationOptions"
                                                        )}
                                                        value={node.options.join(
                                                            ", "
                                                        )}
                                                        xs={6}
                                                    />
                                                )}
                                                {node.conditions.map(
                                                    (condition, index) => (
                                                        <Grid
                                                            item
                                                            xs={12}
                                                            key={`${node.editorKey}-${index}`}
                                                        >
                                                            <Box marginTop={1}>
                                                                <Grid
                                                                    container
                                                                    spacing={1}
                                                                >
                                                                    <SummaryItem
                                                                        label={t(
                                                                            "whatsappApplicationConditionOperator"
                                                                        )}
                                                                        value={t(
                                                                            `whatsappApplicationCondition_${condition.operator}`
                                                                        )}
                                                                        xs={3}
                                                                    />
                                                                    <SummaryItem
                                                                        label={t(
                                                                            "whatsappApplicationConditionValue"
                                                                        )}
                                                                        value={getConditionValue(
                                                                            condition.value
                                                                        )}
                                                                        xs={3}
                                                                    />
                                                                    <SummaryItem
                                                                        label={t(
                                                                            "whatsappApplicationConditionNextNode"
                                                                        )}
                                                                        value={
                                                                            condition.nextNodeId
                                                                        }
                                                                        xs={3}
                                                                    />
                                                                    <SummaryItem
                                                                        label={t(
                                                                            "whatsappApplicationConditionScore"
                                                                        )}
                                                                        value={getConditionValue(
                                                                            condition.scoreDelta
                                                                        )}
                                                                        xs={3}
                                                                    />
                                                                    <SummaryItem
                                                                        label={t(
                                                                            "whatsappApplicationConditionEliminatory"
                                                                        )}
                                                                        value={
                                                                            condition.eliminatory
                                                                                ? t(
                                                                                      "whatsappApplicationYes"
                                                                                  )
                                                                                : t(
                                                                                      "whatsappApplicationNo"
                                                                                  )
                                                                        }
                                                                        xs={3}
                                                                    />
                                                                </Grid>
                                                            </Box>
                                                        </Grid>
                                                    )
                                                )}
                                            </Grid>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </React.Fragment>
                    )}
                </Grid>
            </Box>
        </Paper>
    );
};

export default WhatsappApplicationSummary;
